import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, PhoneCall, CalendarDays } from "lucide-react";
import { Reveal } from "./Reveal";
import { nextWeekdays, formatSlot, Chip } from "./BookingParts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INITIAL = { name: "", email: "", phone: "", notes: "", preferred_time: "" };

const inputClass =
  "w-full bg-transparent border-b border-black/[0.12] focus:border-[#DBB89C] outline-none py-4 text-base tracking-wide placeholder:text-[#908B85]/60 transition-colors duration-300";

export const Booking = () => {
  const [mode, setMode] = useState("scheduled");
  const [form, setForm] = useState(INITIAL);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [slots, setSlots] = useState([]);
  const [taken, setTaken] = useState([]);
  const [sending, setSending] = useState(false);

  const days = useMemo(() => nextWeekdays(10), []);
  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  useEffect(() => {
    if (!date) return;
    setTime(null);
    axios
      .get(`${API}/bookings/availability`, { params: { date } })
      .then((res) => {
        setSlots(res.data.slots);
        setTaken(res.data.taken);
      })
      .catch(() => toast.error("Could not load availability. Please try again."));
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "scheduled" && (!date || !time)) {
      toast.error("Please choose a date and time for your call.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        booking_type: mode,
        ...form,
        date: mode === "scheduled" ? date : null,
        time: mode === "scheduled" ? time : null,
      });
      toast.success(
        mode === "scheduled"
          ? "Your call is booked. Caitlin will confirm shortly — talk soon!"
          : "Request received. Caitlin will call you back as soon as possible."
      );
      setForm(INITIAL);
      setDate(null);
      setTime(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="py-32 md:py-48">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center mb-4">
          <Reveal className="md:col-span-7">
            <p
              data-testid="booking-overline"
              className="text-xs font-semibold tracking-[0.35em] uppercase text-[#CFAEA5] mb-6"
            >
              Book A Call
            </p>
            <h2
              data-testid="booking-heading"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-balance"
            >
              A conversation, <em className="italic text-[#CFAEA5]">on your time.</em>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed tracking-wide text-[#908B85]">
              Choose a time that suits you — weekdays, 9:00am to 8:00pm — or
              request a call back and Caitlin will phone you as soon as she's free.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-4 md:col-start-9">
            <div
              className="overflow-hidden rounded-[4px] aspect-[4/3] shadow-[0_4px_40px_-10px_rgba(0,0,0,0.1)]"
              data-testid="booking-image-frame"
            >
              <img
                src="https://customer-assets-jai6qajn.emergentagent.net/job_mortgage-elite/artifacts/d3qn042l_StraviaLending_Caitlin_128.jpg"
                alt="Caitlin Morrison working from her living room"
                data-testid="booking-image"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setMode("scheduled")}
              data-testid="booking-mode-scheduled"
              className={`inline-flex items-center gap-3 px-6 py-3 rounded-full text-xs font-semibold tracking-[0.2em] uppercase border transition-colors duration-300 ${
                mode === "scheduled"
                  ? "bg-[#DBB89C] border-[#DBB89C] text-white"
                  : "border-black/15 text-[#908B85] hover:border-[#493E38] hover:text-[#493E38]"
              }`}
            >
              <CalendarDays size={15} strokeWidth={1.5} />
              Book a time
            </button>
            <button
              type="button"
              onClick={() => setMode("callback")}
              data-testid="booking-mode-callback"
              className={`inline-flex items-center gap-3 px-6 py-3 rounded-full text-xs font-semibold tracking-[0.2em] uppercase border transition-colors duration-300 ${
                mode === "callback"
                  ? "bg-[#DBB89C] border-[#DBB89C] text-white"
                  : "border-black/15 text-[#908B85] hover:border-[#493E38] hover:text-[#493E38]"
              }`}
            >
              <PhoneCall size={15} strokeWidth={1.5} />
              Request a call back
            </button>
          </div>
        </Reveal>

        <form onSubmit={submit} data-testid="booking-form" className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24 items-start">
          <Reveal delay={0.2} className="md:col-span-6">
            {mode === "scheduled" ? (
              <div className="space-y-10">
                <div>
                  <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#908B85] mb-4">
                    1. Choose a day
                  </p>
                  <div className="flex flex-wrap gap-2.5" data-testid="booking-day-list">
                    {days.map((d) => (
                      <Chip
                        key={d.iso}
                        active={date === d.iso}
                        onClick={() => setDate(d.iso)}
                        testid={`booking-day-${d.iso}`}
                      >
                        {d.label}
                      </Chip>
                    ))}
                  </div>
                </div>
                {date && (
                  <div>
                    <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#908B85] mb-4">
                      2. Choose a time
                    </p>
                    <div className="flex flex-wrap gap-2.5" data-testid="booking-slot-list">
                      {slots.map((t) => (
                        <Chip
                          key={t}
                          active={time === t}
                          disabled={taken.includes(t)}
                          onClick={() => setTime(t)}
                          testid={`booking-slot-${t}`}
                        >
                          {formatSlot(t)}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#908B85]">
                  When suits you best?
                </p>
                <input
                  value={form.preferred_time}
                  onChange={update("preferred_time")}
                  placeholder="e.g. Weekday evenings after 6pm (optional)"
                  data-testid="booking-preferred-time-input"
                  className={inputClass}
                />
                <p className="text-sm leading-relaxed text-[#908B85] max-w-md">
                  Leave your details and Caitlin will call you back personally —
                  usually the same business day.
                </p>
              </div>
            )}
          </Reveal>

          <Reveal delay={0.25} className="md:col-span-5 md:col-start-8">
            <div className="overflow-hidden bg-white rounded-[4px] border border-black/[0.08] shadow-[0_4px_40px_-10px_rgba(0,0,0,0.06)]">
              <div className="plaid h-2.5 w-full" aria-hidden="true" />
              <div className="space-y-8 p-8 md:p-12">
              <input
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Full name"
                data-testid="booking-name-input"
                className={inputClass}
              />
              <input
                required
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="Phone number"
                data-testid="booking-phone-input"
                className={inputClass}
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="Email address"
                data-testid="booking-email-input"
                className={inputClass}
              />
              <textarea
                rows={2}
                value={form.notes}
                onChange={update("notes")}
                placeholder="Anything you'd like to cover? (optional)"
                data-testid="booking-notes-input"
                className={`${inputClass} resize-none`}
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="booking-submit-button"
                className="group inline-flex items-center gap-4 rounded-full bg-[#DBB89C] text-white px-8 py-4 text-xs font-semibold tracking-[0.25em] uppercase transition-colors duration-300 hover:bg-[#493E38] disabled:opacity-50"
              >
                {sending ? "Sending…" : mode === "scheduled" ? "Book My Call" : "Request Call Back"}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              {mode === "scheduled" && date && time && (
                <p data-testid="booking-summary" className="text-xs tracking-wide text-[#CFAEA5]">
                  Booking: {days.find((d) => d.iso === date)?.label} at {formatSlot(time)}
                </p>
              )}
              </div>
            </div>
          </Reveal>
        </form>
      </div>
    </section>
  );
};
